// Simulates a backend database like Supabase
const initialData = {
  users: [
    { id: 'u1', name: 'John Client', email: 'client@libr.com', password: 'password', role: 'client', subscription_status: 'active', assigned_advisor_id: 'a1', applied_voucher: null },
    { id: 'u2', name: 'Sarah Client', email: 'sarah@libr.com', password: 'password', role: 'client', subscription_status: 'inactive', assigned_advisor_id: 'a1', applied_voucher: null },
    { id: 'a1', name: 'Alice Advisor', email: 'advisor@libr.com', password: 'password', role: 'advisor' },
    { id: 'ad1', name: 'Admin User', email: 'admin@libr.com', password: 'password', role: 'admin' },
  ],
  documents: [
    {
      id: 'd1', user_id: 'u1', type: 'will', status: 'draft',
      form_data: { currentStep: 0, fullName: 'John Client', notes: 'Primary beneficiary is my spouse.' },
      advisor_notes: '', created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
    },
    {
      id: 'd2', user_id: 'u1', type: 'immigration_poa', status: 'in_review',
      form_data: {
        currentStep: 6, parentFullName: 'John Client', parentDOB: '1985-03-15',
        parentCountry: 'Mexico', parentImmigrationStatus: 'daca',
        parentAddress: '123 Main St, Los Angeles, CA 90001',
        children: [
          { name: 'Maria Client', dob: '2015-07-20', relationship: 'Daughter' },
          { name: 'Carlos Client', dob: '2018-11-05', relationship: 'Son' },
        ],
        guardian1FullName: 'Rosa Garcia', guardian1Relationship: 'Aunt',
        guardian1Address: '456 Oak Ave, Los Angeles, CA 90002', guardian1Phone: '(555) 123-4567', guardian1Accepts: true,
        guardian2FullName: 'Miguel Reyes', guardian2Relationship: 'Godparent',
        guardian2Address: '789 Pine St, Los Angeles, CA 90003', guardian2Phone: '(555) 987-6543', guardian2Accepts: true,
        scopeOfAuthority: ['medical', 'school', 'travel'],
        effectiveDate: '2025-01-01', expirationOption: 'revoked', triggeringCondition: 'detention',
      },
      advisor_notes: '', created_at: new Date(Date.now() - 86400000).toISOString(), updated_at: new Date(Date.now() - 3600000).toISOString(),
    },
    {
      id: 'd3', user_id: 'u2', type: 'trust', status: 'finalized',
      form_data: { currentStep: 3, fullName: 'Sarah Client', notes: 'Trust for minor children.' },
      advisor_notes: 'Reviewed and confirmed all beneficiary details with client on 2025-01-10.',
      created_at: new Date(Date.now() - 172800000).toISOString(), updated_at: new Date(Date.now() - 86400000).toISOString(),
    },
  ],
  vouchers: [
    { id: 'v1', code: 'LEGACY50', label: '50% Off', discount_type: 'percent', value: 50, is_active: true, times_redeemed: 3 },
    { id: 'v2', code: 'LIBR100', label: 'Full Access Free', discount_type: 'full', value: 100, is_active: true, times_redeemed: 1 },
    { id: 'v3', code: 'INSURANCE25', label: '25% Off', discount_type: 'percent', value: 25, is_active: true, times_redeemed: 7 },
    { id: 'v4', code: 'FREEWILL2024', label: 'Legacy Promo', discount_type: 'full', value: 100, is_active: false, times_redeemed: 12 },
  ],
  notes: [],
};

if (!localStorage.getItem('libr_db')) {
  localStorage.setItem('libr_db', JSON.stringify(initialData));
}

// Migration: ensure all vouchers have times_redeemed
const migrateDB = () => {
  const data = JSON.parse(localStorage.getItem('libr_db'));
  let changed = false;
  data.vouchers = data.vouchers.map((v) => {
    if (v.times_redeemed === undefined) { changed = true; return { ...v, times_redeemed: 0 }; }
    return v;
  });
  // Ensure new voucher codes exist
  const existingCodes = data.vouchers.map((v) => v.code);
  const newVouchers = [
    { id: 'v_legacy50', code: 'LEGACY50', label: '50% Off', discount_type: 'percent', value: 50, is_active: true, times_redeemed: 3 },
    { id: 'v_libr100', code: 'LIBR100', label: 'Full Access Free', discount_type: 'full', value: 100, is_active: true, times_redeemed: 1 },
    { id: 'v_ins25', code: 'INSURANCE25', label: '25% Off', discount_type: 'percent', value: 25, is_active: true, times_redeemed: 7 },
  ];
  newVouchers.forEach((nv) => {
    if (!existingCodes.includes(nv.code)) { data.vouchers.push(nv); changed = true; }
  });
  if (!data.users[0].hasOwnProperty('applied_voucher')) {
    data.users = data.users.map((u) => ({ ...u, applied_voucher: u.applied_voucher || null }));
    changed = true;
  }
  if (changed) localStorage.setItem('libr_db', JSON.stringify(data));
};
migrateDB();

const getDB = () => JSON.parse(localStorage.getItem('libr_db'));
const saveDB = (data) => localStorage.setItem('libr_db', JSON.stringify(data));

export const db = {
  login: async (email, password) => {
    await new Promise((r) => setTimeout(r, 500));
    const data = getDB();
    const user = data.users.find((u) => u.email === email && u.password === password);
    if (!user) throw new Error('Invalid credentials');
    const { password: _, ...userWithoutPassword } = user;
    return userWithoutPassword;
  },

  getUser: async (id) => {
    await new Promise((r) => setTimeout(r, 200));
    const data = getDB();
    const user = data.users.find((u) => u.id === id);
    if (!user) return null;
    const { password: _, ...userWithoutPassword } = user;
    return userWithoutPassword;
  },

  getDocumentsByUser: async (userId) => {
    await new Promise((r) => setTimeout(r, 300));
    return getDB().documents.filter((d) => d.user_id === userId);
  },

  getAllDocuments: async () => {
    await new Promise((r) => setTimeout(r, 300));
    return getDB().documents;
  },

  getDocument: async (id) => {
    await new Promise((r) => setTimeout(r, 300));
    return getDB().documents.find((d) => d.id === id);
  },

  createDocument: async (userId, type) => {
    await new Promise((r) => setTimeout(r, 400));
    const data = getDB();
    const newDoc = {
      id: `d${Date.now()}`, user_id: userId, type, status: 'draft',
      form_data: { currentStep: 0 }, advisor_notes: '',
      created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
    };
    data.documents.push(newDoc);
    saveDB(data);
    return newDoc;
  },

  updateDocument: async (id, updates) => {
    await new Promise((r) => setTimeout(r, 400));
    const data = getDB();
    const index = data.documents.findIndex((d) => d.id === id);
    if (index === -1) throw new Error('Document not found');
    data.documents[index] = { ...data.documents[index], ...updates, updated_at: new Date().toISOString() };
    saveDB(data);
    return data.documents[index];
  },

  getClientsForAdvisor: async (advisorId) => {
    await new Promise((r) => setTimeout(r, 300));
    return getDB().users.filter((u) => u.assigned_advisor_id === advisorId);
  },

  getAllUsers: async () => {
    await new Promise((r) => setTimeout(r, 300));
    return getDB().users;
  },

  // Voucher methods
  getAllVouchers: async () => {
    await new Promise((r) => setTimeout(r, 300));
    return getDB().vouchers;
  },

  redeemVoucher: async (code, userId) => {
    await new Promise((r) => setTimeout(r, 600));
    const data = getDB();
    const voucher = data.vouchers.find((v) => v.code.toUpperCase() === code.toUpperCase());
    if (!voucher) throw new Error('Invalid voucher code. Please check and try again.');
    if (!voucher.is_active) throw new Error('This voucher code has expired or been deactivated.');
    const user = data.users.find((u) => u.id === userId);
    if (user?.applied_voucher) throw new Error('You have already applied a voucher to your account.');
    // Update voucher redemption count
    const vIdx = data.vouchers.findIndex((v) => v.id === voucher.id);
    data.vouchers[vIdx].times_redeemed = (data.vouchers[vIdx].times_redeemed || 0) + 1;
    // Update user
    const uIdx = data.users.findIndex((u) => u.id === userId);
    data.users[uIdx].applied_voucher = voucher.code;
    data.users[uIdx].subscription_status = 'active';
    saveDB(data);
    return voucher;
  },

  toggleVoucherStatus: async (voucherId) => {
    await new Promise((r) => setTimeout(r, 300));
    const data = getDB();
    const idx = data.vouchers.findIndex((v) => v.id === voucherId);
    if (idx === -1) throw new Error('Voucher not found');
    data.vouchers[idx].is_active = !data.vouchers[idx].is_active;
    saveDB(data);
    return data.vouchers[idx];
  },
};